import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import type { SelfServiceSubmissionRow } from '../../types/selfService';
import { useAdminContext } from '../../components/self-service/admin/useAdminContext';
import { scopeForContext } from '../../components/self-service/admin/stationScopeRequest';
import AdminPageHeader from '../../components/self-service/admin/AdminPageHeader';
import { ALL_STATIONS_SLUG } from '../../components/self-service/admin/lastAdminStation';
import { useAuth } from '../../components/self-service/auth/useAuth';
import { rankOf } from '../../components/self-service/shared/roleRank';
import { publisherStatusBadge } from '../../components/self-service/campaignSharedHelpers';
import {
  isSwapPending,
  needsPublisherReview,
} from '../../components/self-service/submissionReviewPredicates';
import MetricsSection from '../../components/self-service/metrics/MetricsSection';
import { apiClient } from '../../lib/apiClient';

type SubmissionRow = Pick<
  SelfServiceSubmissionRow,
  | 'id'
  // CTV-759: each row carries its own station, so a merged review queue can
  // link to the right station's detail page without the page's slug.
  | 'publisher_slug'
  | 'station_name'
  | 'business_info'
  | 'contact_email'
  | 'campaign'
  | 'amount_cents'
  | 'status'
  | 'created_at'
  | 'creatives'
>;

const PublisherAdminPage: React.FC = () => {
  const ctx = useAdminContext();
  const aggregated = ctx.mode === 'aggregated';
  // The scope's slug segment — the sentinel in aggregated mode. Used for the
  // header selector and for the "View all" links, which stay in scope.
  const scopeSlug = aggregated ? ALL_STATIONS_SLUG : ctx.slug;
  const scope = scopeForContext(ctx);
  const { user } = useAuth();
  const [submissions, setSubmissions] = React.useState<SubmissionRow[]>([]);
  // CTV-673 audit: submissions are manager+ (the API refuses sales_rep on
  // every submissions route), so the review queues below are theirs alone
  // and a rep never fetches them.
  // CTV-808: a rep's Overview is the Metrics section only, narrowed
  // server-side to the campaigns credited to their referral code. It is
  // station-bound (a salesperson record belongs to one station), so the
  // header offers no All stations and an aggregated URL collapses.
  const isManagerPlus = rankOf(user?.rawRole) >= rankOf('manager');
  const scopeKey = JSON.stringify(scope);

  React.useEffect(() => {
    if (!isManagerPlus) return;
    (async () => {
      try {
        const { rows } = await apiClient.post<{ rows: SubmissionRow[] }>(
          '/submissions/list-by-publisher',
          JSON.parse(scopeKey),
        );
        setSubmissions(rows ?? []);
      } catch {
        /* non-fatal — overview tiles tolerate empty list */
      }
    })();
  }, [scopeKey, isManagerPlus]);

  // CTV-743: the payout-method banner that used to live here has moved to
  // PayoutSetupBanner, rendered by PublisherAdminLayout on this route. It was
  // asking the wrong question (stripe_payouts_enabled alone), which is
  // silence on exactly the stations that cannot be paid, and it flashed on
  // every load because an unresolved fetch read as "not connected".
  //
  // CTV-759: both queues merge across stations in aggregated mode rather than
  // filtering down. A work queue that hides work when the view widens fails
  // in the wrong direction. needsPublisherReview is station-agnostic.
  const needsReview = submissions.filter(needsPublisherReview);
  // Recent submissions: drop drafts (not actionable) and rows already surfaced
  // in Needs your review (avoid double-rendering).
  const recent = submissions
    .filter((s) => s.status !== 'pending_payment' && !needsPublisherReview(s))
    .slice(0, 5);

  if (!isManagerPlus && ctx.mode === 'aggregated') {
    // CTV-808: a typed /admin/all lands a rep on the collapse target. The
    // API would narrow an aggregated read correctly, but the rep view is
    // station-bound by decision, and the sidebar never offers the sentinel.
    return <Navigate to={`/admin/${ctx.collapseSlug}`} replace />;
  }

  return (
    <div className="max-w-5xl">
      <AdminPageHeader
        title="Overview"
        subtitle={
          aggregated
            ? `Combined view across ${ctx.stations.length} stations.`
            : isManagerPlus
              ? undefined
              : 'Delivery and revenue for the campaigns credited to your referral code.'
        }
        scopeMode={isManagerPlus ? 'flexible' : 'bound'}
        currentSlug={scopeSlug}
      />

      {/* CTV-808: the same section for every role; the API narrows a rep's
          numbers to their own referrals. Reps have no Billing tab, so the
          popover's pointer to it is dropped for them. */}
      <MetricsSection scope={scope} showBillingHint={isManagerPlus} />

      {needsReview.length > 0 && (
        <section className="mb-8">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
              Needs your review · {needsReview.length}
            </h2>
            <Link
              to={`/admin/${scopeSlug}/submissions?tab=review`}
              className="text-sm text-blue-600 hover:underline"
            >
              View all →
            </Link>
          </div>
          <ul className="space-y-2">
            {needsReview.map((s) => (
              <SubmissionListRow key={s.id} s={s} showStation={aggregated} />
            ))}
          </ul>
        </section>
      )}

      {recent.length > 0 && (
        <div className="mb-8">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Recent submissions</h2>
            <Link to={`/admin/${scopeSlug}/submissions`} className="text-sm text-blue-600 hover:underline">View all →</Link>
          </div>
          <ul className="space-y-2">
            {recent.map((s) => (
              <SubmissionListRow key={s.id} s={s} showStation={aggregated} />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// CTV-759: the row's slug comes from the row (`publisher_slug`), not the
// page. Clicking a merged-queue row is a Scope collapse to that row's
// station, because acting on a submission happens on a station-bound page.
function SubmissionListRow({ s, showStation }: { s: SubmissionRow; showStation: boolean }) {
  const badge = publisherStatusBadge(s.status);
  return (
    <li>
      <Link
        to={`/admin/${s.publisher_slug}/submissions/${s.id}`}
        className="flex items-center justify-between gap-3 bg-white border border-slate-200 rounded-xl p-3 hover:border-slate-400 hover:shadow-sm transition-all"
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="text-sm font-medium text-slate-900 truncate">
              {s.business_info?.businessName || 'Advertiser'}
            </span>
            <span className={`text-xs font-medium px-2 py-0.5 rounded ${badge.className}`}>
              {badge.label}
            </span>
            {showStation && (s.station_name || s.publisher_slug) && (
              <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200">
                {s.station_name || s.publisher_slug}
              </span>
            )}
            {isSwapPending(s) && (
              <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200">
                Creative swap
              </span>
            )}
          </div>
          <div className="text-xs text-slate-500">
            ${(s.amount_cents / 100).toFixed(2)} · {new Date(s.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
          </div>
        </div>
        <ChevronRight className="w-4 h-4 text-slate-400" />
      </Link>
    </li>
  );
}

export default PublisherAdminPage;
